/**
 * Memory Review Panel v2
 *
 * Displays client memory with 3 tabs:
 * - Profile: Applied facts with confidence indicators
 * - Suggestions: Pending approvals with source snippets
 * - Notes: Note entries with timestamps
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '../api/orchestrator';

interface MemoryProfile {
  id: string;
  tenant_id: string;
  contact_id: string;
  profile_jsonb: Record<string, any>;
  profile_version: number;
  total_facts_applied: number;
  total_notes_count: number;
  last_updated: string;
  pending_facts_count: number;
  pending_suggestions_count: number;
}

interface MemorySuggestion {
  id: string;
  field_key: string;
  field_label: string;
  field_path: string[];
  proposed_value_jsonb: any;
  existing_value_jsonb: any;
  confidence: number;
  extraction_type: string;
  source_message_id: string;
  source_snippet: string;
  source_message_created_at: string;
  status: string;
}

interface MemoryNote {
  id: string;
  note_text: string;
  note_type: string;
  tags: string[];
  char_count: number;
  created_at: string;
  source_message_id: string;
}

interface MemoryReviewPanelProps {
  tenantId: string;
  contactId: string;
  chatId?: string;
  onMemoryUpdate?: () => void;
  reviewedBy: string; // User ID or email
}

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8090';

export const MemoryReviewPanel: React.FC<MemoryReviewPanelProps> = ({
  tenantId,
  contactId,
  chatId: _chatId,
  onMemoryUpdate,
  reviewedBy,
}) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'suggestions' | 'notes'>('profile');
  const [profile, setProfile] = useState<MemoryProfile | null>(null);
  const [suggestions, setSuggestions] = useState<MemorySuggestion[]>([]);
  const [notes, setNotes] = useState<MemoryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  // Fetch memory data
  useEffect(() => {
    fetchMemoryData();
  }, [tenantId, contactId]);

  const fetchMemoryData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch profile
      const profileRes = await authFetch(
        `${API_BASE}/api/memory/v2/profile?tenant_id=${encodeURIComponent(tenantId)}&contact_id=${encodeURIComponent(contactId)}`
      );
      const profileData = await profileRes.json();
      if (profileData.ok) {
        setProfile(profileData.profile);
      }

      // Fetch suggestions
      const suggestionsRes = await authFetch(
        `${API_BASE}/api/memory/v2/suggestions?tenant_id=${encodeURIComponent(tenantId)}&contact_id=${encodeURIComponent(contactId)}&status=pending`
      );
      const suggestionsData = await suggestionsRes.json();
      if (suggestionsData.ok) {
        setSuggestions(suggestionsData.suggestions || []);
      }

      // Fetch notes
      const notesRes = await authFetch(
        `${API_BASE}/api/memory/v2/notes?tenant_id=${encodeURIComponent(tenantId)}&contact_id=${encodeURIComponent(contactId)}&limit=20`
      );
      const notesData = await notesRes.json();
      if (notesData.ok) {
        setNotes(notesData.notes || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load memory data');
    } finally {
      setLoading(false);
    }
  };

  // Approve suggestion
  const handleApprove = async (suggestionId: string, edits?: any) => {
    setProcessing(prev => new Set(prev).add(suggestionId));

    try {
      const res = await authFetch(`${API_BASE}/api/memory/v2/suggestions/${suggestionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewed_by: reviewedBy,
          edits,
        }),
      });

      const data = await res.json();
      if (data.ok) {
        // Refresh data
        await fetchMemoryData();
        onMemoryUpdate?.();
      } else {
        setError(data.error || 'Failed to approve suggestion');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to approve suggestion');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(suggestionId);
        return next;
      });
    }
  };

  // Reject suggestion
  const handleReject = async (suggestionId: string, reason?: string) => {
    setProcessing(prev => new Set(prev).add(suggestionId));

    try {
      const res = await authFetch(`${API_BASE}/api/memory/v2/suggestions/${suggestionId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewed_by: reviewedBy,
          reason,
        }),
      });

      const data = await res.json();
      if (data.ok) {
        // Refresh data
        await fetchMemoryData();
        onMemoryUpdate?.();
      } else {
        setError(data.error || 'Failed to reject suggestion');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reject suggestion');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(suggestionId);
        return next;
      });
    }
  };

  // Format value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  if (loading) {
    return (
      <div style={{
        width: '350px',
        height: '100%',
        background: '#f9fafb',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ color: '#6b7280' }}>Loading memory...</div>
      </div>
    );
  }

  return (
    <div style={{
      width: '350px',
      height: '100%',
      background: '#f9fafb',
      borderLeft: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem',
        borderBottom: '1px solid #e5e7eb',
        background: '#ffffff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
            🧠 Client Memory
          </h3>
          <button
            onClick={fetchMemoryData}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            title="Refresh"
          >
            🔄
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={() => setActiveTab('profile')}
            style={{
              flex: 1,
              padding: '0.5rem',
              fontSize: '0.8rem',
              fontWeight: '500',
              background: activeTab === 'profile' ? '#eff6ff' : 'transparent',
              border: activeTab === 'profile' ? '1px solid #3b82f6' : '1px solid #e5e7eb',
              borderRadius: '4px',
              cursor: 'pointer',
              color: activeTab === 'profile' ? '#1e40af' : '#6b7280',
            }}
          >
            Profile
            {profile && profile.pending_facts_count > 0 && (
              <span style={{
                marginLeft: '0.25rem',
                padding: '0.1rem 0.35rem',
                background: '#fef3c7',
                borderRadius: '10px',
                fontSize: '0.7rem',
              }}>
                {profile.pending_facts_count}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            style={{
              flex: 1,
              padding: '0.5rem',
              fontSize: '0.8rem',
              fontWeight: '500',
              background: activeTab === 'suggestions' ? '#fef3c7' : 'transparent',
              border: activeTab === 'suggestions' ? '1px solid #f59e0b' : '1px solid #e5e7eb',
              borderRadius: '4px',
              cursor: 'pointer',
              color: activeTab === 'suggestions' ? '#92400e' : '#6b7280',
            }}
          >
            Suggestions
            {suggestions.length > 0 && (
              <span style={{
                marginLeft: '0.25rem',
                padding: '0.1rem 0.35rem',
                background: '#fee2e2',
                borderRadius: '10px',
                fontSize: '0.7rem',
              }}>
                {suggestions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            style={{
              flex: 1,
              padding: '0.5rem',
              fontSize: '0.8rem',
              fontWeight: '500',
              background: activeTab === 'notes' ? '#f0fdf4' : 'transparent',
              border: activeTab === 'notes' ? '1px solid #10b981' : '1px solid #e5e7eb',
              borderRadius: '4px',
              cursor: 'pointer',
              color: activeTab === 'notes' ? '#065f46' : '#6b7280',
            }}
          >
            Notes ({notes.length})
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '0.75rem',
          margin: '1rem',
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          fontSize: '0.85rem',
          color: '#991b1b',
        }}>
          {error}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {activeTab === 'profile' && (
          <ProfileTab
            profile={profile}
            formatValue={formatValue}
          />
        )}

        {activeTab === 'suggestions' && (
          <SuggestionsTab
            suggestions={suggestions}
            processing={processing}
            onApprove={handleApprove}
            onReject={handleReject}
            formatValue={formatValue}
          />
        )}

        {activeTab === 'notes' && (
          <NotesTab
            notes={notes}
          />
        )}
      </div>
    </div>
  );
};

// ================================
// PROFILE TAB
// ================================

interface ProfileTabProps {
  profile: MemoryProfile | null;
  formatValue: (value: any) => string;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ profile, formatValue }) => {
  // Local utility function
  const getFieldLabel = (key: string): string => {
    const labels: Record<string, string> = {
      'name': 'Name',
      'phone': 'Phone Number',
      'email': 'Email Address',
      'family': 'Family Members',
      'mother': 'Mother',
      'father': 'Father',
      'spouse': 'Spouse',
      'children': 'Children',
      'inquiry': 'Inquiry Type',
      'preferences': 'Preferences',
      'case_notes': 'Case Notes',
    };
    return labels[key] || key;
  };

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧠</div>
        <div style={{ fontSize: '0.9rem' }}>No memory profile yet</div>
        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
          Information will be extracted from conversations
        </div>
      </div>
    );
  }

  const profileData = profile.profile_jsonb || {};

  // Flatten nested objects for display
  const flattenObject = (obj: any, prefix = ''): Array<{ key: string; value: any; path: string[] }> => {
    const result: Array<{ key: string; value: any; path: string[] }> = [];

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const path = prefix ? [...prefix.split('.'), key] : [key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result.push(...flattenObject(value, fullKey));
      } else if (value !== null && value !== undefined && value !== '') {
        result.push({ key: fullKey, value, path });
      }
    }

    return result;
  };

  const fields = flattenObject(profileData);

  return (
    <div>
      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.5rem',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
            {Object.keys(profileData).length}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Fields</div>
        </div>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
            {profile.total_facts_applied}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Facts</div>
        </div>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
            {profile.total_notes_count}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Notes</div>
        </div>
      </div>

      {/* Last updated */}
      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1rem', textAlign: 'center' }}>
        Last updated: {new Date(profile.last_updated).toLocaleString()}
      </div>

      {/* Fields */}
      {fields.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af' }}>
          No information stored yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {fields.map((field) => (
            <div
              key={field.key}
              style={{
                padding: '0.75rem',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                  {getFieldLabel(field.key)}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                  {field.key}
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#1f2937', wordBreak: 'break-word' }}>
                {formatValue(field.value)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ================================
// SUGGESTIONS TAB
// ================================

interface SuggestionsTabProps {
  suggestions: MemorySuggestion[];
  processing: Set<string>;
  onApprove: (id: string, edits?: any) => void;
  onReject: (id: string, reason?: string) => void;
  formatValue: (value: any) => string;
}

const SuggestionsTab: React.FC<SuggestionsTabProps> = ({
  suggestions,
  processing,
  onApprove,
  onReject,
  formatValue,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedValue, setEditedValue] = useState<string>('');

  // Local utility functions
  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.85) return { color: '#10b981', label: 'High', bg: '#d1fae5' };
    if (confidence >= 0.65) return { color: '#f59e0b', label: 'Med', bg: '#fef3c7' };
    return { color: '#ef4444', label: 'Low', bg: '#fee2e2' };
  };

  const getFieldLabel = (key: string): string => {
    const labels: Record<string, string> = {
      'name': 'Name',
      'phone': 'Phone Number',
      'email': 'Email Address',
      'family': 'Family Members',
      'mother': 'Mother',
      'father': 'Father',
      'spouse': 'Spouse',
      'children': 'Children',
      'inquiry': 'Inquiry Type',
      'preferences': 'Preferences',
      'case_notes': 'Case Notes',
    };
    return labels[key] || key;
  };

  if (suggestions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✨</div>
        <div style={{ fontSize: '0.9rem' }}>No pending suggestions</div>
        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
          All extracted information has been processed
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {suggestions.map((suggestion) => {
        const badge = getConfidenceBadge(suggestion.confidence);
        const isProcessing = processing.has(suggestion.id);
        const isEditing = editingId === suggestion.id;

        return (
          <div
            key={suggestion.id}
            style={{
              padding: '1rem',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>
                {getFieldLabel(suggestion.field_key)}
              </span>
              <span
                style={{
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  background: badge.bg,
                  color: badge.color,
                  fontWeight: '600',
                }}
              >
                {badge.label} ({Math.round(suggestion.confidence * 100)}%)
              </span>
            </div>

            {/* Source snippet */}
            {suggestion.source_snippet && (
              <div style={{
                padding: '0.5rem',
                marginBottom: '0.75rem',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: '#6b7280',
                fontStyle: 'italic',
              }}>
                "{suggestion.source_snippet.substring(0, 100)}{suggestion.source_snippet.length > 100 ? '...' : ''}"
              </div>
            )}

            {/* Values */}
            <div style={{ marginBottom: '0.75rem' }}>
              {suggestion.existing_value_jsonb !== null && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.15rem' }}>Current:</div>
                  <div style={{
                    padding: '0.5rem',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#991b1b',
                  }}>
                    {formatValue(suggestion.existing_value_jsonb)}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.15rem' }}>Proposed:</div>
                {isEditing ? (
                  <textarea
                    value={editedValue}
                    onChange={(e) => setEditedValue(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      minHeight: '60px',
                      fontFamily: 'monospace',
                    }}
                  />
                ) : (
                  <div style={{
                    padding: '0.5rem',
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#065f46',
                  }}>
                    {formatValue(suggestion.proposed_value_jsonb)}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      try {
                        const edits = JSON.parse(editedValue);
                        onApprove(suggestion.id, edits);
                        setEditingId(null);
                        setEditedValue('');
                      } catch {
                        alert('Invalid JSON format');
                      }
                    }}
                    disabled={isProcessing}
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.6 : 1,
                    }}
                  >
                    ✓ Save & Approve
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditedValue('');
                    }}
                    disabled={isProcessing}
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => onApprove(suggestion.id)}
                    disabled={isProcessing}
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.6 : 1,
                    }}
                  >
                    ✓ Approve
                  </button>
                  {suggestion.extraction_type === 'structured_llm' && typeof suggestion.proposed_value_jsonb === 'object' && (
                    <button
                      onClick={() => {
                        setEditingId(suggestion.id);
                        setEditedValue(JSON.stringify(suggestion.proposed_value_jsonb, null, 2));
                      }}
                      disabled={isProcessing}
                      style={{
                        flex: 1,
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      ✏️ Edit
                    </button>
                  )}
                  <button
                    onClick={() => onReject(suggestion.id)}
                    disabled={isProcessing}
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.6 : 1,
                    }}
                  >
                    ✕ Reject
                  </button>
                </>
              )}
            </div>

            {/* Timestamp */}
            <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9ca3af' }}>
              {new Date(suggestion.source_message_created_at).toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ================================
// NOTES TAB
// ================================

interface NotesTabProps {
  notes: MemoryNote[];
}

const NotesTab: React.FC<NotesTabProps> = ({ notes }) => {
  if (notes.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#9ca3af' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
        <div style={{ fontSize: '0.9rem' }}>No notes yet</div>
        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
          Notes will be extracted from conversations
        </div>
      </div>
    );
  }

  const getNoteTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'general': '#6b7280',
      'case_summary': '#3b82f6',
      'client_story': '#8b5cf6',
      'travel_history': '#f59e0b',
      'appeal_context': '#ef4444',
      'preference_note': '#10b981',
    };
    return colors[type] || '#6b7280';
  };

  const getNoteTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'general': 'General',
      'case_summary': 'Case Summary',
      'client_story': 'Client Story',
      'travel_history': 'Travel History',
      'appeal_context': 'Appeal Context',
      'preference_note': 'Preference',
    };
    return labels[type] || type;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {notes.map((note) => (
        <div
          key={note.id}
          style={{
            padding: '1rem',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span
              style={{
                fontSize: '0.7rem',
                padding: '0.15rem 0.5rem',
                borderRadius: '4px',
                background: '#f3f4f6',
                color: getNoteTypeColor(note.note_type),
                fontWeight: '600',
              }}
            >
              {getNoteTypeLabel(note.note_type)}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
              {new Date(note.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Note text */}
          <div style={{
            padding: '0.75rem',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: '#1f2937',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
          }}>
            {note.note_text}
          </div>

          {/* Tags */}
          {note.tags && note.tags.length > 0 && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {note.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: '0.7rem',
                    padding: '0.15rem 0.4rem',
                    background: '#e5e7eb',
                    borderRadius: '4px',
                    color: '#374151',
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Char count */}
          <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9ca3af' }}>
            {note.char_count} characters
          </div>
        </div>
      ))}
    </div>
  );
};

export default MemoryReviewPanel;
